import React from 'react';
import IntlMessages from 'lngProvider/IntlMessages';
import FormContainer from 'containers/FormContainer';
import { TextField, Button, Typography, Grid, Divider } from '@mui/material';
import * as Yup from 'yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import GoogleLogo from 'components/svgs/GoogleLogo';
import FacebookLogo from 'components/svgs/FacebookLogo';
import MicrosoftLogo from 'components/svgs/MicrosoftLogo';
import AppleLogo from 'components/svgs/AppleLogo';
import MMMLogo from 'components/svgs/MMMLogo';
import { EmailValidation } from 'services/EnumConstants'
import { styled } from '@mui/material/styles';
import { colors, spacing } from 'styles/Common';

const StyledGrid = styled(Grid)((
    {
        theme
    }
    ) => {
        return {
            maxWidth: '300px',
            marginTop: spacing[7],
            '& a': {
                color: theme.palette.primary.lighter
            },
            '& .MuiInputBase-input': {
                height: 30
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: 48
            },
            '& .MuiButton-root': {
                borderRadius: '8px'
            },
            '& .MuiButton-contained.Mui-disabled': {
                backgroundColor: theme.palette.primary.light
            },
            '& .title-text': {
                fontWeight: 600,
                marginBottom: spacing[2]
            },
            '& .subtitle-text': {
                marginBottom: spacing[2],
                color: colors.fillWhite
            },
            '& .social-button': {
                backgroundColor: colors.fillWhite,
                height: 53,
                width: 68,
                borderWidth: '1px',
                borderColor: theme.palette.secondary.main
            },
            '& .text-field': {
                height: 48,
                marginTop: spacing[1]
            },
            '& .forgot-password': {
                marginTop: spacing[1],
                marginRight: spacing[2],
                textAlign: 'right'
            },
            '& .or-text': {
                textAlign: 'center',
                color: theme.palette.secondary.light,
                marginBottom: spacing[5]
            },
            '& .divider-line': {
                textAlign: 'center',
                color: theme.palette.secondary.light,
                margin: spacing[2]
            },
            '& .sign-in-button': {
                color: colors.fillWhite,
                marginBottom: spacing[7],
                height: 48,
                '& .MuiButton-contained.Mui-disabled': {
                    backgroundColor: theme.palette.primary.light
                }
            },
            '& .create-account-text': {
                textAlign: 'left',
                color: theme.palette.secondary.light,
                marginTop: spacing[9]
            }
        };
    });

const LoginMobile = (props) => {
    const emailRegex = EmailValidation.Production;

    const validationSchema = Yup.object().shape({
        email: Yup
            .string()
            .trim()
            .required(<IntlMessages id="logIn.invalidEmailAddress" />)
            .email(<IntlMessages id="logIn.invalidEmailAddress" />)
            .matches(emailRegex, () => <IntlMessages id="logIn.invalidEmailAddress" />),
        password: Yup
            .string()
            .required(<IntlMessages id="logIn.invalidPassword" />)
            .min(8, true)
    });

    const { data, loginConfig, onSubmit, onSendActivationEmail, onPropsChange, onHandleGoogle, onHandleFacebook, onHandleMicrosoft, onHandleApple, onSsoClick, onEmailChanged, intl } = props;
    const registrationMessage = props.intl.formatMessage({ id: loginConfig.createAccountText }).replace(`{regUrl}`, loginConfig.registrationPageUrl);
    
    return (
        <StyledGrid container alignItems="center" justifyContent="center" direction="column">
            <Grid item>
                <Typography variant="h3" className="title-text"><IntlMessages id={loginConfig.loginTitle} /></Typography>
                {loginConfig.loginSubTitle ?
                    <Typography variant="subtitle2"><IntlMessages id={loginConfig.loginSubTitle} /></Typography>
                    : ''}
            </Grid>
            <FormContainer
                initialValues={data}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {props => {
                    const {
                        values,
                        errors,
                        touched,
                        isSubmitting,
                        handleChange,
                        handleBlur
                    } = props;

                    onPropsChange(props);

                    const handleEmailChanged = (e) => {

                        // pass through to parent
                        handleChange(e);

                        // invoke event handler
                        if (onEmailChanged) {
                            onEmailChanged(e);
                        }
                    }

                    return (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    value={values.email}
                                    onChange={handleEmailChanged}
                                    error={errors.email && touched.email}
                                    onBlur={handleBlur}
                                    helperText={((errors.email && touched.email) && errors.email)}
                                    name="email"
                                    maxLength="256"
                                    placeholder={intl.formatMessage({ id: 'logIn.email' })}
                                    className="text-field"
                                    fullWidth />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={values.password}
                                    onChange={handleChange}
                                    error={errors.password && touched.password}
                                    onBlur={handleBlur}
                                    helperText={((errors.password && touched.password) && errors.password)}
                                    type="password"
                                    name="password"
                                    maxLength="256"
                                    placeholder={intl.formatMessage({ id: 'logIn.password' })}
                                    className="text-field"
                                    fullWidth />
                            </Grid>
                            {values.logInErrorId
                                ? <Grid item xs={12}>
                                    <Typography variant="subtitle2">
                                        <IntlMessages id={values.logInErrorId} />
                                    </Typography>
                                </Grid>
                                : ''}
                            {values.resendActivationEmailVisible === true
                                ? <Grid container spacing={4}>
                                    {values.resendActivationEmailSuccessful === true
                                        ? <Grid item xs={12}>
                                            <CheckCircleIcon className="text-success" /> <IntlMessages id="logIn.resendActivationEmailSuccess" />
                                        </Grid>
                                        : ''}
                                    {values.resendActivationEmailFailed === true
                                        ? <Grid item xs={12}>
                                            <ErrorIcon className="text-danger" /> <IntlMessages id="logIn.resendActivationEmailFailure" />
                                        </Grid>
                                        : ''}
                                    {!values.resendActivationEmailSuccessful
                                        ? <Grid item xs={12}>
                                            <InfoIcon className="text-info" />
                                            <IntlMessages id="logIn.needsActivation" />
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                disabled={values.resendActivationEmailInProgress}
                                                startIcon={<EmailIcon />}
                                                onClick={onSendActivationEmail}>
                                                <IntlMessages id="logIn.resendActivationEmail" />
                                            </Button>
                                        </Grid>
                                        : ''}
                                </Grid>
                                : ''}
                            {!values.resendActivationEmailVisible
                                ? <Grid container spacing={4}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" className="forgot-password">
                                            <a href={`/app/forgotPassword${window.location.search}`}>
                                                <IntlMessages id="logIn.forgotPassword" />
                                            </a>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            type="submit"
                                            className="sign-in-button"
                                            fullWidth
                                            disabled={isSubmitting ||
                                                !values.password ||
                                                values.password === '' ||
                                                !!errors.password ||
                                                !values.email ||
                                                values.email === '' ||
                                                !!errors.email}><IntlMessages id={loginConfig.loginButtonText} /></Button>
                                    </Grid>
                                    {loginConfig.showGoogle === true ||
                                        loginConfig.showApple === true ||
                                        loginConfig.showFacebook === true ||
                                        loginConfig.showMicrosoft === true ||
                                        loginConfig.showMMMSso === true
                                        ?
                                        <Grid container>
                                            <Grid item xs={4}>
                                                <Divider className="divider-line" />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2" className="or-text">
                                                    <IntlMessages id="logIn.orContinueWith" />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Divider className="divider-line" />
                                            </Grid>
                                        </Grid>
                                    :''}
                                    {loginConfig.showGoogle === true ?
                                        <Grid item xs={3}>
                                            <Button
                                                variant="outlined"
                                                disableElevation={true}
                                                onClick={onHandleGoogle}
                                                className="social-button">
                                                <GoogleLogo />
                                            </Button>
                                        </Grid>
                                        : ''}
                                    {loginConfig.showApple === true ?
                                        <Grid item xs={3}>
                                            <Button
                                                variant="outlined"
                                                disableElevation={true}
                                                onClick={onHandleApple}
                                                className="social-button">
                                                <AppleLogo />
                                            </Button>
                                        </Grid>
                                        : ''}
                                    {loginConfig.showFacebook === true ?
                                        <Grid item xs={3}>
                                            <Button
                                                variant="outlined"
                                                disableElevation={true}
                                                onClick={onHandleFacebook}
                                                className="social-button">
                                                <FacebookLogo />
                                            </Button>
                                        </Grid>
                                        : ''}
                                    {loginConfig.showMicrosoft === true ?
                                        <Grid item xs={3}>
                                            <Button
                                                variant="outlined"
                                                disableElevation={true}
                                                onClick={() => onHandleMicrosoft(values)}
                                                className="social-button">
                                                <MicrosoftLogo />
                                            </Button>
                                        </Grid>
                                        : ''}
                                    {loginConfig.showMMMSso ?
                                        <Grid item xs={12}>
                                            <Button
                                                variant="outlined"
                                                disableElevation={true}
                                                onClick={onSsoClick}
                                                className="social-button"
                                                startIcon={<MMMLogo />}>
                                            </Button>
                                        </Grid>
                                        : ''}
                                    {loginConfig.registrationPageUrl && loginConfig ?
                                        <Grid item xs={12} className="create-account-text">
                                            <Grid container alignItems="center" justifyContent="center" direction="column">
                                                <Grid item>
                                                    <Typography variant="subtitle1" dangerouslySetInnerHTML={{ __html: registrationMessage }} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        : ''}
                                </Grid>
                                : ''}
                        </Grid>
                    );

                }}
            </FormContainer>
        </StyledGrid>
    );
}

export default LoginMobile;